import { createApp } from 'vue';
import { Settings } from 'luxon';
import App from './App.vue';
import router from './router';

import { IonicVue } from '@ionic/vue';
import { Capacitor } from '@capacitor/core';
import OneSignal, {
  NotificationClickEvent,
  NotificationWillDisplayEvent,
} from 'onesignal-cordova-plugin';

import './tailwind.css';
import './styles/form-reset.css';
import '@ionic/vue/css/core.css';
import { redirectNotificationOrAlertToRessource } from '@/services/notification-router.service';
import { customTransition } from '@/animations/router.transition';
import * as Sentry from '@sentry/capacitor';
import packageJson from '../package.json';
import { Keyboard } from '@capacitor/keyboard';
import { isLoggedIn } from '@/services/auth';
import { loginOneSignal } from '@/services/onesignal.service';

Settings.defaultLocale = 'fr';
Settings.defaultZone = 'Europe/Paris';

const app = createApp(App)
  .use(IonicVue, {
    animated: true,
    navAnimation: customTransition,
  })
  .use(router);

Keyboard.setResizeMode({
  mode: 'ionic',
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
  tracesSampleRate: 1.0,
  environment: import.meta.env.MODE,
  enabled: import.meta.env.MODE !== 'development',
  release: packageJson.version,

  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (err instanceof Error) {
      if (err.name === 'CanceledError' || (err as any).code === 'ERR_CANCELED') return null;
      if (err.name === 'AbortError') return null;
    }
    return event;
  },
});

let oneSignalInitialized = false;

router.isReady().then(() => {
  app.mount('#app');

  // ⚠️ OneSignal uniquement sur natif
  if (!Capacitor.isNativePlatform()) return;

  // Guard contre la double-registration (HMR en dev)
  if (oneSignalInitialized) return;
  oneSignalInitialized = true;

  OneSignal.Debug.setLogLevel(6);

  OneSignal.initialize(import.meta.env.VITE_ONESIGNAL_APP_ID);

  isLoggedIn().then((loggedIn) => {
    if (loggedIn) loginOneSignal();
  });

  OneSignal.Notifications.requestPermission(false);

  OneSignal.Notifications.addEventListener(
    'foregroundWillDisplay',
    (event: NotificationWillDisplayEvent) => {
      event.preventDefault();
      event.getNotification().display();
    },
  );

  OneSignal.Notifications.addEventListener('click', async (event: NotificationClickEvent) => {
    const data = event?.notification?.additionalData;

    if (!data) {
      console.warn('⚠️ Pas de additionalData');
      return;
    }

    try {
      // 🔥 Attendre que le router soit prêt
      await router.isReady();

      // 🔥 Attendre que la WebView soit totalement stabilisée (ANDROID IMPORTANT)
      await new Promise((resolve) => setTimeout(resolve, 800));

      // @ts-ignore
      redirectNotificationOrAlertToRessource(data);
    } catch (err) {
      console.error('❌ Erreur redirection notification:', err);
    }
  });
});
